import _, { groupBy } from 'lodash';
import {
  getRatioFromQty,
  isWorkCategory,
  shouldDisplayAsRatio,
} from 'spw-components/worksheet/utils';
import {
  ICategoryNote,
  IWorksheetRow,
} from 'spw-redux/modules/calculators/types';
import { CUSTOM_SUBCATEGORY, STORAGE_LOCKER } from 'spw-constants';
import { Category, Subcategory } from 'spw-redux/modules/space_types/types';
import {
  IProjectSettings,
  RatioCalculationType,
} from 'spw-redux/modules/settings/types';
import { sortRowsByPreviousId } from 'spw-redux/modules/calculators/utils';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import ProgramScenario, { PlainProgramScenario } from './Scenario';
import Worksheet, { PlainWorksheet, WorksheetTotals } from './Worksheet';
import WorksheetRow from './WorksheetRow';
import WorksheetStorageSpaceRow from './WorksheetStorageSpaceRow';

function isStorageRow(a: WorksheetRow): a is WorksheetStorageSpaceRow {
  return a?.isStorageSupportSpace;
}

export default class TotalProgramWorksheet {
  scenario: ProgramScenario | PlainProgramScenario;

  subtotalProgramUSF = 0;

  workspacesSeats = 0;

  workspacesUSF = 0;

  rows: WorksheetRow[];

  sharedSpacesAssignedSpacePercentage: number;

  sharedSpacesUSF?: number;

  categoriesTotals: Record<string, WorksheetTotals> = {};

  subcategoriesTotals: Record<string, WorksheetTotals> = {};

  spaceTypesTotals: Record<string, WorksheetTotals> = {};

  rowsByCategory: Record<Category | string, WorksheetRow[]> = {
    individualWorkspaces: [],
    meetingSpaces: [],
    amenitySpaces: [],
    supportSpaces: [],
    customCategory: [],
  };

  worksheetsMap: Record<number, Worksheet | PlainWorksheet> = {};

  rowsBySubcategory: Record<
    Subcategory | string,
    WorksheetRow[]
  > = {} as Record<Subcategory | string, WorksheetRow[]>;

  hasStorageRows?: boolean;

  hasNestedRows = true; // true by default bc a TPS will always have linked dept scenario rows

  hasWorkspacesNotIncludedInTotalSeats?: boolean;

  breakDownRows: Record<string, WorksheetRow[]> = {};

  notes: ICategoryNote[];

  private readonly projectSettings: IProjectSettings;

  constructor(
    worksheets: Worksheet[] | PlainWorksheet[],
    scenario: ProgramScenario | PlainProgramScenario,
    projectSettings: IProjectSettings,
    totalProgramRows?: WorksheetRow[]
  ) {
    this.scenario = scenario;
    this.projectSettings = projectSettings;

    worksheets.forEach((ws) => {
      if (!ws) return;
      this.subtotalProgramUSF += ws.subtotalProgramUSF;
      this.workspacesSeats += ws.workspacesSeats;
      this.workspacesUSF += ws.workspacesUSF;
      this.worksheetsMap[ws.id] = ws;
    });

    if (totalProgramRows?.length) {
      this.rows = this.getTotalProgramRows([
        { id: scenario.id, rows: totalProgramRows },
      ]);
    } else {
      this.rows = this.getTotalProgramRows(worksheets);
    }

    this.rows.forEach((row) => {
      this.placeIntoCorrespondingCategory(row);
      if (this.shouldExcludeRowForTotals(row)) return;
      this.increaseCategoryTotals(row);
      if (row.subcategory) this.increaseSubcategoryTotals(row);
      this.increaseSpaceTypesTotals(row);
    });
    Object.keys(this.spaceTypesTotals).forEach((spaceTypeName: string) => {
      const quantity =
        this.spaceTypesTotals[spaceTypeName].lockersOrDrawersQuantity ||
        this.spaceTypesTotals[spaceTypeName].spacesQuantity;
      const dividend =
        this.spaceTypesTotals[spaceTypeName].dividendForRatio ||
        this.scenario.workSeats;
      this.spaceTypesTotals[spaceTypeName].ratio = dividend / quantity;
    });
    this.notes = scenario.notes;
  }

  shouldExcludeRowForTotals = (row: WorksheetRow) => {
    const isDetailedScenario = this.scenario.isDetailed;
    if (row.removed) return true;
    return (
      isWorkCategory(row) &&
      ((!isDetailedScenario && row.child) || (isDetailedScenario && !row.child))
    );
  };

  increaseCategoryTotals = (row: WorksheetRow) => {
    const previousTotals = this.categoriesTotals[row.category] || {
      USF: 0,
      seats: 0,
      spacesQuantity: 0,
      seatMix: 0,
    };

    const shouldExcludeRowFromTotalQuantity = !!row.isStorageSupportSpace;
    const newQuantity = shouldExcludeRowFromTotalQuantity
      ? previousTotals.spacesQuantity
      : previousTotals.spacesQuantity + row.quantity;
    this.categoriesTotals[row.category] = {
      USF: previousTotals.USF + row.usf * row.quantity,
      seats:
        previousTotals.seats +
        (row.isIncludedInTotalWorkSeats ? row.seatCap * row.quantity : 0),
      spacesQuantity: newQuantity,
    };
  };

  increaseSubcategoryTotals = (row: WorksheetRow) => {
    const previousTotals = this.subcategoriesTotals[row.subcategory] || {
      USF: 0,
      seats: 0,
      spacesQuantity: 0,
    };

    this.subcategoriesTotals[row.subcategory] = {
      USF: previousTotals.USF + row.usf * row.quantity,
      seats: previousTotals.seats + row.seatCap * row.quantity,
      spacesQuantity: previousTotals.spacesQuantity + row.quantity,
    };
  };

  increaseSpaceTypesTotals = (row: WorksheetRow) => {
    if (row.removed) return;
    if (!this.scenario.isDetailed && !row.isStorageSupportSpace) {
      if (!row.child)
        this.spaceTypesTotals[row.uniqueKey] = {
          USF: row.usf * row.quantity,
          seats: row.seatCap * row.quantity,
          spacesQuantity: row.quantity,
          seatMix: row.ratio,
        };

      return;
    }
    if (!row.spaceType.parent) return;
    const parentSpaceTypeKey = `${row.spaceType.parent.name}_${row.spaceType.parent.id}`;

    const previousTotals = this.spaceTypesTotals[parentSpaceTypeKey] || {
      USF: 0,
      seats: 0,
      spacesQuantity: 0,
      seatMix: 0,
      lockersOrDrawersQuantity: 0,
    };

    this.spaceTypesTotals[parentSpaceTypeKey] = {
      USF: previousTotals.USF + row.usf * row.quantity,
      seats: previousTotals.seats + row.seatCap * row.quantity,
      spacesQuantity: previousTotals.spacesQuantity + row.quantity,
      seatMix: previousTotals.seatMix + row.ratio,
      lockersOrDrawersQuantity:
        previousTotals.lockersOrDrawersQuantity +
          (row as WorksheetStorageSpaceRow).lockersOrDrawersQuantity || 0,
      dividendForRatio: isStorageRow(row)
        ? this.getDividendForRatio(row.isLocker)
        : 0,
    };
  };

  private getTotalProgramRows = (
    worksheets:
      | Worksheet[]
      | PlainWorksheet[]
      | { id: number; rows: WorksheetRow[] }[]
  ) => {
    const isDetailedScenario = this.scenario.isDetailed;
    const wsMap: {
      [wsId: number]:
        | Worksheet
        | PlainWorksheet
        | { id: number; rows: WorksheetRow[] };
    } = {};

    const flatWorksheetRows = _(worksheets)
      .compact()
      .flatMap((worksheet) => {
        wsMap[worksheet.id] = worksheet;
        return worksheet.rows || [];
      })
      .value();

    const worksheetRowSpaceTypes = _(flatWorksheetRows)
      .uniqBy((row) => `${!!row.child}-${row?.spaceType?.id}`)
      .groupBy((row: WorksheetRow) => {
        if (row.subcategory) {
          return row.subcategory;
        }

        return row.category;
      })
      .values()
      .map((rows: WorksheetRow[]) => {
        const parentRows = sortRowsByPreviousId(
          rows.filter((row) => !row.child)
        );

        const childRowsBySpaceType = groupBy(
          rows.filter((row) => row.child),
          (row: WorksheetRow) => {
            return row.projectSpaceType.parent.id;
          }
        );

        return parentRows.reduce((categoryRows, parentRow: IWorksheetRow) => {
          const nestedRows =
            childRowsBySpaceType[parentRow.projectSpaceType.spaceType.id];
          if (nestedRows) {
            return [...categoryRows, parentRow, ...nestedRows];
          }
          return [...categoryRows, parentRow];
        }, []);
      })
      .flatten()
      .value();

    const output: WorksheetRow[] = [];
    worksheetRowSpaceTypes.forEach(
      ({
        spaceType,
        child,
        category,
        subcategory,
        id,
        isIncludedInTotalWorkSeats,
        isStorageSupportSpace,
        projectSpaceTypeCategory,
        projectSpaceTypeSubcategory,
        projectSpaceType,
        ...rest
      }) => {
        const relatedWorksheetRows = _.filter(
          flatWorksheetRows,
          (row) =>
            !!child === !!row.child &&
            row?.spaceType?.id === spaceType?.id &&
            row.quantity * row.usf > 0
        );
        let removedCount = 0;
        const {
          totalQuantity,
          totalSeats,
          totalUsf,
        } = relatedWorksheetRows.reduce(
          (acc, r) => {
            if (r.removed) removedCount += 1;
            return {
              totalUsf: acc.totalUsf + r.quantity * r.usf,
              totalQuantity: acc.totalQuantity + r.quantity,
              totalSeats: acc.totalSeats + r.seatCap * r.quantity,
            };
          },
          {
            totalQuantity: 0,
            totalSeats: 0,
            totalUsf: 0,
          }
        );

        const removed = relatedWorksheetRows.length
          ? removedCount === relatedWorksheetRows.length
          : false;

        const aggregateSeatCap = totalSeats / totalQuantity;

        // this is the correct usf to display on the total program worksheet, as it's the weighted average of all of the
        // child scenario rows
        const aggregateUsf = totalUsf / totalQuantity;

        let row: WorksheetRow | WorksheetStorageSpaceRow = {
          spaceType,
          name: spaceType?.name,
          id,
          child,
          isLocker: spaceType.spaceType === STORAGE_LOCKER,
          sizeX: 0,
          sizeY: 0,
          seatCap:
            !_.isNaN(aggregateSeatCap) && _.isFinite(aggregateSeatCap)
              ? aggregateSeatCap
              : 0,
          usf:
            !_.isNaN(aggregateUsf) && _.isFinite(aggregateUsf)
              ? aggregateUsf
              : 0,
          quantity: totalQuantity,
          category,
          projectSpaceType,
          projectSpaceTypeCategory,
          subcategory,
          projectSpaceTypeSubcategory,
          ratio: 0,
          removed,
          isIncludedInTotalWorkSeats,
          isStorageSupportSpace,
          totalUsf,
          uniqueKey: `${spaceType?.name}_${spaceType?.id}`,
        };

        let shouldAddRow = row.quantity * row.usf > 0;

        if (
          !row.child &&
          (isStorageRow(row) ||
            (isDetailedScenario &&
              isIWCategoryOrSubcategory(row.projectSpaceTypeCategory)))
        ) {
          const childRows = flatWorksheetRows.filter(
            (childRow) =>
              childRow.child === true &&
              childRow.spaceType.parent.id === row.spaceType.id
          );

          const sumOfChildRowsTotalUsf = (childRows || []).reduce(
            (total, childRow) => {
              return total + childRow.quantity * childRow.usf;
            },
            0
          );

          if (isStorageRow(row)) {
            row.dividendForRatio = this.getDividendForRatio(row.isLocker);
            row = {
              ...rest,
              ...row,
            };
          }
          // Parent row is only shown when at least one child has non-zero totalUsf
          shouldAddRow = sumOfChildRowsTotalUsf > 0;
        }

        if (
          !row.removed &&
          subcategory === CUSTOM_SUBCATEGORY &&
          row.quantity * row.usf > 0 &&
          !projectSpaceTypeSubcategory.includeInTotalWorkSeats
        )
          this.hasWorkspacesNotIncludedInTotalSeats = true;

        if (isStorageRow(row) && isStorageRow(relatedWorksheetRows[0])) {
          this.hasStorageRows = true;
          let lockersOrDrawersQuantity = 0;
          const dividendForRatio = this.getDividendForRatio(row.isLocker);
          const firstRelatedRow = relatedWorksheetRows[0] as WorksheetStorageSpaceRow;

          relatedWorksheetRows.forEach((r) => {
            if (isStorageRow(r)) {
              lockersOrDrawersQuantity += r.lockersOrDrawersQuantity;
            }
          });

          row.lockersOrDrawersRatioLabel =
            firstRelatedRow?.lockersOrDrawersRatioLabel;
          row.storageTypeLabel = firstRelatedRow.storageTypeLabel;
          row.storageUnitRatioLabel = firstRelatedRow.storageUnitRatioLabel;
          row.storageUnitQuantityLabel =
            firstRelatedRow.storageUnitQuantityLabel;
          row.lockersOrDrawersQuantity = lockersOrDrawersQuantity;
          row.lockersOrDrawersPerUnit =
            firstRelatedRow?.lockersOrDrawersPerUnit;
          row.dividendForRatio = dividendForRatio;
          // Assign correct numerator to parent space type
          const parentSpaceTypeKey = `${row.spaceType.name}_${row.spaceType.id}`;
          if (!row.child && this.spaceTypesTotals[parentSpaceTypeKey])
            this.spaceTypesTotals[
              parentSpaceTypeKey
            ].dividendForRatio = dividendForRatio;
          row.ratio = getRatioFromQty(
            row.lockersOrDrawersQuantity,
            dividendForRatio
          );
          row.storageUnitsRatio = getRatioFromQty(
            row.quantity,
            dividendForRatio
          );
          row.usableSquareInches = (row.usf * 144) / 2;
        } else if (!isStorageRow(row)) {
          row.ratio = this.calculateRatio(row);
        }
        // back out the row ratio, depending on what type of space the row is

        if (shouldAddRow) {
          output.push(row);
        } else {
          return;
        }

        const shouldNotHaveDepartmentChildRows =
          !child &&
          ((isIWCategoryOrSubcategory(projectSpaceTypeCategory) &&
            isDetailedScenario) ||
            isStorageSupportSpace);

        if (
          !removed &&
          relatedWorksheetRows.length &&
          !shouldNotHaveDepartmentChildRows
        ) {
          const breakDownRows: WorksheetRow[] = [];

          relatedWorksheetRows.forEach((r) => {
            if (r.removed) return;

            breakDownRows.push({
              ...r,
              child: true,
              title: this.worksheetsMap[r.worksheetId]?.scenario?.primaryLabel,
              subtitle: this.worksheetsMap[r.worksheetId]?.scenario
                ?.secondaryLabel,
            });
          });

          this.breakDownRows[row.uniqueKey] = breakDownRows;
        }
      }
    );

    return output;
  };

  calculateRatio = (row: WorksheetRow, dividendForRatio?: number) => {
    return shouldDisplayAsRatio(
      row.projectSpaceTypeCategory,
      row.projectSpaceTypeSubcategory
    )
      ? getRatioFromQty(
          row.quantity,
          dividendForRatio || this.scenario.workSeats
        )
      : (100 * row.quantity * row.seatCap) / this.scenario.workSeats;
  };

  placeIntoCorrespondingCategory = (row: WorksheetRow) => {
    if (row.removed) return;

    if (this.rowsByCategory[row.category as Category])
      this.rowsByCategory[row.category as Category].push(row);
    else this.rowsByCategory[row.category] = [row];

    if (row.subcategory) {
      if (this.rowsBySubcategory[row.subcategory])
        this.rowsBySubcategory[row.subcategory].push(row);
      else this.rowsBySubcategory[row.subcategory] = [row];
    }
  };

  getDividendForRatio = (isLocker: boolean) => {
    switch (
      isLocker
        ? this.projectSettings?.lockerRatioCalculationType
        : this.projectSettings?.fileCabinetRatioCalculationType
    ) {
      case RatioCalculationType.HEADCOUNT:
        return this.scenario.headcount;
      case RatioCalculationType.HEADCOUNT_WITH_UTILIZATION:
        return this.scenario.headcountWithUtilization;
      case RatioCalculationType.HEADCOUNT_WITH_VACANCY:
        return this.scenario.headcountWithVacancy;
      default:
        return this.scenario.workSeats;
    }
  };

  toPlainObject = (): PlainWorksheet => JSON.parse(JSON.stringify(this));
}
